import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Footer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLinkClick = (path) => {
    if (path === "/") {
      navigate("/");
      window.scrollTo(0, 0);
    } else if (path.startsWith("/#")) {
      const sectionId = path.substring(2);
      navigate("/");
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  const quickLinks = [
    { id: "home", label: t("nav.home"), path: "/" },
    { id: "about", label: t("nav.about"), path: "/#about" },
    { id: "partners", label: t("nav.partners"), path: "/#partners" },
    { id: "activities", label: t("nav.activities"), path: "/activities" },
    { id: "resources", label: t("nav.resources"), path: "/resources" },
  ];

  const projectLinks = [
    { id: "home", label: t("nav.home"), path: "/" },
    { id: "about", label: t("nav.about"), path: "/#about" },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-emerald-500/5 via-transparent to-teal-500/5"></div>
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand section */}
          <div className="md:col-span-2">
            <div className="flex items-center mb-6 group">
              <img
                src="/dca-logo.png"
                alt="DCA Project Logo"
                className="h-12 w-auto mr-4 transition-transform duration-300 group-hover:scale-110"
                loading="lazy"
              />
              <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                {t("footer.title")}
              </h3>
            </div>
            <p className="text-gray-300 mb-8 text-lg leading-relaxed max-w-md">
              {t("footer.description")}
            </p>
          </div>

          {/* Project links */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-emerald-400">
              {t("footer.project")}
            </h3>
            <ul className="space-y-3">
              {projectLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => handleLinkClick(link.path)}
                    className="text-gray-300 hover:text-emerald-400 transition-all duration-300 hover:translate-x-2 inline-block group"
                  >
                    <span className="group-hover:underline">{link.label}</span>
                    <svg
                      className="inline-block w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-emerald-400">
              {t("footer.quickLinks")}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => handleLinkClick(link.path)}
                    className="text-gray-300 hover:text-emerald-400 transition-all duration-300 hover:translate-x-2 inline-block group"
                  >
                    <span className="group-hover:underline">{link.label}</span>
                    <svg
                      className="inline-block w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom section */}
        <div className="mt-16 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row items-center justify-between">
            {/* EU Logo */}
            <div className="flex items-center mb-6 md:mb-0">
              <img
                src="/partners/eu-logo-white.png"
                alt={t("home.projectInfo.fundedByValue")}
                className="h-12 w-auto"
                loading="lazy"
              />
            </div>

            {/* Copyright */}
            <div className="text-center md:text-right">
              <p className="text-white text-sm mb-2 ">
                {t("footer.disclaimer")}
              </p>
              <p className="text-white text-xs">{t("footer.projectInfo")}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
